import React from 'react';
import { createBrowserRouter, redirect } from 'react-router';
import { loadInitialState } from '#stores/AuthContext';

import DashboardLayout from '../layouts/DashboardLayout';
import PrivateRoute from './PrivateRoute';

const RouteErrorElement = () =>
  React.createElement(
    'div',
    {
      className:
        'min-h-screen flex flex-col items-center justify-center p-8 text-center',
    },
    React.createElement(
      'h1',
      { className: 'text-2xl font-semibold text-[#0F172A] mb-4' },
      'Something went wrong'
    ),
    React.createElement(
      'p',
      { className: 'text-gray-600 mb-6 max-w-md' },
      'An unexpected error occurred while loading this section. Please try refreshing the page or go back to the dashboard.'
    ),
    React.createElement(
      'div',
      { className: 'flex gap-3' },
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: () => window.location.reload(),
          className: 'btn btn-secondary',
        },
        'Refresh'
      ),
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: () => {
            window.location.href = '/dashboard';
          },
          className: 'btn btn-outline',
        },
        'Go to Dashboard'
      )
    )
  );

const routes = createBrowserRouter([
  {
    index: true,
    lazy: async () => ({
      Component: (await import('../pages/website/WebSite')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
  },
  {
    path: 'terms-of-service',
    lazy: async () => ({
      Component: (await import('../pages/website/TermsOfServicePage')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
  },
  {
    path: 'privacy-policy',
    lazy: async () => ({
      Component: (await import('../pages/website/PrivacyPolicyPage')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
  },
  {
    path: 'auth',
    lazy: async () => ({
      Component: (await import('../pages/login/Login')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
    loader: () => {
      const initialState = loadInitialState();
      if (initialState.isAuthenticated) {
        throw redirect('/dashboard');
      }
      return null;
    },
  },
  {
    path: 'set-password',
    lazy: async () => ({
      Component: (await import('../pages/login/SetPassword')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
  },
  {
    path: 'dashboard',
    Component: PrivateRoute,
    errorElement: React.createElement(RouteErrorElement),
    children: [
      {
        path: '',
        Component: DashboardLayout,
        errorElement: React.createElement(RouteErrorElement),
        children: [
          {
            path: 'categories',
            lazy: async () => ({
              Component: (await import('../pages/Categories')).default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            index: true,
            path: '',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/Library')).default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            path: 'library',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/Library')).default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            path: 'admin/scenario',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/admin/Admin'))
                .default,
            }),
            errorElement: React.createElement(RouteErrorElement),
            children: [
              {
                path: 'new-scenario',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/NewScenario')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'global-slide-creator',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/GlobalSlideCreator')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-global-slides',
                lazy: async () => ({
                  Component: (
                    await import('../components/core/admin/scenarios/ManageGlobalSlides')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'edit/:id',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/EditScenario')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-scenarios',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/ManageScenarios')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-categories',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/ManageCategories')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-tags',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/scenarios/ManageTags')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
            ],
          },
          {
            path: 'admin/companies',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/admin/Admin'))
                .default,
            }),
            errorElement: React.createElement(RouteErrorElement),
            children: [
              {
                path: 'new-company',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/companies/NewCompany')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'edit/:id',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/companies/EditCompany')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-companies',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/companies/ManageCompanies')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
            ],
          },
          {
            path: 'admin/users',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/admin/Admin'))
                .default,
            }),
            errorElement: React.createElement(RouteErrorElement),
            children: [
              {
                path: 'new-user',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/users/NewUser')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'manage-users',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/users/ManageUsers')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
              {
                path: 'edit/:id',
                lazy: async () => ({
                  Component: (
                    await import('../pages/dashboard/admin/users/EditUser')
                  ).default,
                }),
                errorElement: React.createElement(RouteErrorElement),
              },
            ],
          },
          {
            path: 'favorites',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/Favorites')).default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            path: 'reports',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/reports/Reports'))
                .default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            path: 'reports/:id',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/reports/Report'))
                .default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
          {
            path: 'team',
            lazy: async () => ({
              Component: (await import('../pages/dashboard/Team')).default,
            }),
            errorElement: React.createElement(RouteErrorElement),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    lazy: async () => ({
      Component: (await import('../pages/NotFound')).default,
    }),
    errorElement: React.createElement(RouteErrorElement),
  },
]);

export default routes;
